/**
 * PortCosEditor Component
 *
 * Entity-specific editor for PortCos lookup table
 * Includes company dropdown
 */

import {
  usePortcos,
  useCreatePortco,
  useUpdatePortco,
  useDeletePortco,
  useCompanies,
} from '@medsuite/api-client';

import EntityEditor from '../components/EntityEditor';

interface PortCosEditorProps {
  onDataLoaded?: (count: number) => void;
}
export default function PortCosEditor({ onDataLoaded }: PortCosEditorProps) {
  const { data: portcos = [], isLoading, error } = usePortcos();
  const { data: companies = [] } = useCompanies();
  const addMutation = useCreatePortco();
  const updateMutation = useUpdatePortco();
  const deleteMutation = useDeletePortco();

  const columns = [
    { key: 'portco_id', label: 'ID', readonly: true },
    { key: 'portco_name', label: 'PortCo Name', type: 'text' as const },
    {
      key: 'company_id',
      label: 'Company',
      type: 'select' as const,
      options: companies.map((c) => ({
        value: c.company_id,
        label: c.company_name,
      })),
    },
  ];

  return (
    <EntityEditor
      title="Portfolio Companies"
      columns={columns}
      data={portcos as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
      primaryKey="portco_id"
      onDataLoaded={onDataLoaded}
      onAdd={async (item) => {
        await addMutation.mutateAsync(item as { portco_name: string; company_id: number });
      }}
      onUpdate={async (id, item) => {
        await updateMutation.mutateAsync({
          id: Number(id),
          data: item as Partial<{ portco_name: string; company_id: number }>,
        });
      }}
      onDelete={(id) => deleteMutation.mutateAsync(Number(id))}
      isAdding={addMutation.isPending}
      isUpdating={updateMutation.isPending}
      isDeleting={deleteMutation.isPending}
    />
  );
}
